"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, Mail, Send, Edit2, Sparkles, Loader2, User, AtSign } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";
import { isSpecialtyChatLink } from "@/lib/specialtyChatLinks";
import { trackEmailClick } from "@/lib/analytics";
import { chatAPI } from "@/lib/api";

interface EmailComponentProps {
  emailContent: string;
  recipientEmail?: string;
  patientEmail?: string;
  patientName?: string;
  className?: string;
  chatId?: string;
  sessionId?: string;
  onSendSuccess?: () => void;
}

export function EmailComponent({
  emailContent,
  recipientEmail,
  patientEmail: initialPatientEmail,
  patientName: initialPatientName,
  className,
  chatId,
  sessionId,
  onSendSuccess,
}: EmailComponentProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(emailContent);
  const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);

  // Patient info state - pre-populated if provided
  const [patientName, setPatientName] = useState(initialPatientName || "");
  const [patientEmail, setPatientEmail] = useState(initialPatientEmail || "");

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Extract subject line (first line starting with "Betreff:" or first line by default)
  const extractSubjectAndBody = (content: string) => {
    const lines = content.split("\n");
    const firstLine = lines[0] || "";

    if (firstLine.toLowerCase().startsWith("betreff:")) {
      return {
        subject: firstLine.replace(/^betreff:\s*/i, ""),
        body: lines.slice(1).join("\n").trim(),
      };
    }

    // If no "Betreff:" prefix, treat first line as subject
    return {
      subject: firstLine,
      body: lines.slice(1).join("\n").trim(),
    };
  };

  const { subject, body } = extractSubjectAndBody(editedContent);

  // Convert markdown to plain text for email clients
  const markdownToPlainText = (markdown: string): string => {
    let text = markdown;

    // Convert bold **text** or __text__ to UPPERCASE
    text = text.replace(/\*\*([^*]+)\*\*/g, (_, match) => match.toUpperCase());
    text = text.replace(/__([^_]+)__/g, (_, match) => match.toUpperCase());

    // Convert italic *text* or _text_ to regular text
    text = text.replace(/\*([^*]+)\*/g, "$1");
    text = text.replace(/_([^_]+)_/g, "$1");

    // Convert headers (# Header) to UPPERCASE
    text = text.replace(/^#{1,6}\s+(.+)$/gm, (_, match) => match.toUpperCase());

    // Convert unordered lists
    text = text.replace(/^\s*[-*+]\s+(.+)$/gm, "• $1");

    // Convert ordered lists
    text = text.replace(/^\s*\d+\.\s+(.+)$/gm, (match) => match);

    // Convert links [text](url) to text (url)
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)");

    // Remove code blocks markers
    text = text.replace(/```[^`]*```/g, (match) => {
      return match.replace(/```\w*\n?/g, "").trim();
    });

    // Remove inline code markers
    text = text.replace(/`([^`]+)`/g, "$1");

    // Clean up excessive whitespace
    text = text.replace(/\n{3,}/g, "\n\n");

    return text.trim();
  };

  // Create mailto link as fallback
  const createMailtoLink = () => {
    const plainTextContent = markdownToPlainText(body);
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(plainTextContent);
    const ccEmail = "nils.zaenker@charite.de";
    const emailAddress = recipientEmail || "";
    return `mailto:${emailAddress}?subject=${encodedSubject}&body=${encodedBody}&cc=${ccEmail}`;
  };

  // Auto-resize textarea to fit content
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";
      // Set height to scrollHeight to fit all content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // Adjust textarea height when entering edit mode or content changes
  useEffect(() => {
    if (isEditing) {
      adjustTextareaHeight();
    }
  }, [isEditing, editedContent]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedContent);
      setShowCopyConfirmation(true);
      setTimeout(() => setShowCopyConfirmation(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const handleSendEmail = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Validate patient email
    if (!patientEmail || !patientEmail.includes("@")) {
      return; // Don't send without valid email
    }

    setIsSending(true);

    try {
      const plainTextContent = markdownToPlainText(body);

      const result = await chatAPI.sendEmail({
        recipientEmail: recipientEmail || "",
        patientEmail,
        patientName: patientName || "Patient",
        subject,
        body: plainTextContent,
        chatId,
      });

      if (result.success) {
        setSendSuccess(true);
        if (onSendSuccess) {
          onSendSuccess();
        }

        // Track successful email send
        if (chatId) {
          trackEmailClick({
            chatId,
            sessionId: sessionId || undefined,
            emailRecipient: recipientEmail || undefined,
            emailSubject: subject,
          });
        }
      } else {
        // Silently fall back to mailto on API failure
        console.warn("Email API failed, falling back to mailto:", result.error);
        window.open(createMailtoLink(), "_self");
      }
    } catch (error) {
      // Silently fall back to mailto on error
      console.warn("Email send error, falling back to mailto:", error);
      window.open(createMailtoLink(), "_self");
    } finally {
      setIsSending(false);
    }
  };

  const handleOpenMailClient = (e: React.MouseEvent) => {
    // Prevent event bubbling to avoid double-triggering
    e.stopPropagation();
    e.preventDefault();

    // Track email button click before opening mail client
    if (chatId) {
      trackEmailClick({
        chatId,
        sessionId: sessionId || undefined,
        emailRecipient: recipientEmail || undefined,
        emailSubject: subject,
      });
    }

    // Open default mail client
    window.open(createMailtoLink(), "_self");
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    // Use setTimeout to ensure DOM is updated before adjusting height
    setTimeout(() => adjustTextareaHeight(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsEditing(false);
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  // Check if we can send via API (need patient email)
  const canSendViaApi = patientEmail && patientEmail.includes("@") && recipientEmail;

  // Show success state
  if (sendSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          "rounded-xl bg-green-50 border border-green-200 p-4",
          className
        )}
      >
        <div className="flex items-center gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-green-800">
              E-Mail erfolgreich gesendet!
            </p>
            <p className="text-xs text-green-600 mt-0.5">
              Die Anfrage wurde an {recipientEmail} gesendet.
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={cn(
        "relative rounded-xl bg-(--outer-bg) border border-border shadow-xs hover:shadow-md transition-shadow",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="relative">
            <Mail className="h-4 w-4 text-primary-500" />
          </div>
          <span className="text-sm font-medium text-foreground truncate">
            E-Mail Vorlage
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <motion.button
            onClick={handleCopy}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-background hover:bg-muted text-foreground rounded-lg border border-border shadow-xs hover:shadow-md transition-all"
            title="E-Mail kopieren"
          >
            <AnimatePresence mode="wait">
              {showCopyConfirmation ? (
                <motion.div
                  key="check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="flex items-center gap-1.5"
                >
                  <Check className="h-3.5 w-3.5 text-green-600" />
                  <span className="hidden sm:inline text-green-600">
                    Kopiert!
                  </span>
                </motion.div>
              ) : (
                <motion.div
                  key="copy"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="flex items-center gap-1.5"
                >
                  <Copy className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Kopieren</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* Patient Info Fields (shown when we have recipient but need patient info) */}
      {recipientEmail && (!initialPatientEmail || !initialPatientName) && (
        <div className="px-4 py-3 bg-muted/30 border-b border-border space-y-2">
          <p className="text-xs text-muted-foreground mb-2">
            Bitte geben Sie Ihre Kontaktdaten ein:
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                placeholder="Ihr Name"
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-hidden focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
            <div className="flex-1 relative">
              <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="email"
                value={patientEmail}
                onChange={(e) => setPatientEmail(e.target.value)}
                placeholder="Ihre E-Mail-Adresse"
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-hidden focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="relative">
        {isEditing ? (
          <div className="p-4">
            <textarea
              ref={textareaRef}
              value={editedContent}
              onChange={(e) => {
                setEditedContent(e.target.value);
                adjustTextareaHeight();
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              className="w-full p-3 text-sm border border-border rounded-lg resize-none focus:outline-hidden focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-mono bg-background overflow-hidden"
              placeholder="E-Mail-Inhalt bearbeiten..."
              autoFocus
              style={{ minHeight: "250px" }}
            />
          </div>
        ) : (
          <motion.div
            ref={contentRef}
            onClick={handleStartEdit}
            whileHover={{ backgroundColor: "var(--muted)" }}
            className="relative group cursor-text transition-colors rounded-b-xl overflow-hidden"
            title="Klicken zum Bearbeiten"
          >
            {/* Hover indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
              className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2 py-1 bg-background/90 backdrop-blur-xs rounded-md border border-border shadow-xs"
            >
              <Edit2 className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Bearbeiten</span>
            </motion.div>

            {/* Subject Line */}
            <div className="px-4 py-3 bg-muted/50 border-b border-border">
              <div className="flex items-start gap-2">
                <span className="text-xs font-semibold text-muted-foreground">
                  Betreff:
                </span>
                <span className="text-sm font-medium text-foreground flex-1">
                  {subject}
                </span>
              </div>
            </div>

            {/* Email Body */}
            <div className="px-4 py-3 text-sm text-foreground leading-relaxed">
              <div className="prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-1 prose-strong:text-foreground prose-headings:text-foreground">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Customize rendering for better email compatibility
                    h1: ({ children }) => (
                      <h1 className="text-lg font-bold mb-2 mt-3">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-base font-bold mb-2 mt-3">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-sm font-bold mb-1 mt-2">{children}</h3>
                    ),
                    p: ({ children }) => <p className="mb-2">{children}</p>,
                    ul: ({ children }) => (
                      <ul className="list-disc pl-5 mb-2">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal pl-5 mb-2">{children}</ol>
                    ),
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                    em: ({ children }) => <em className="italic">{children}</em>,
                    hr: () => <hr className="my-3 border-border" />,
                    a: ({ href, children }) => isSpecialtyChatLink(href) ? (
                      <span>{children}</span>
                    ) : (
                      <a
                        href={href}
                        className="text-primary-500 hover:text-primary-600 underline"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {body || editedContent}
                </ReactMarkdown>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="px-4 py-3 bg-muted/30 border-t border-border">
        <div className="flex items-center gap-2">
          {canSendViaApi ? (
            <motion.button
              onClick={handleSendEmail}
              disabled={isSending}
              whileHover={{ scale: isSending ? 1 : 1.02 }}
              whileTap={{ scale: isSending ? 1 : 0.98 }}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg shadow-xs transition-all",
                isSending
                  ? "bg-primary-400 text-white cursor-wait"
                  : "bg-linear-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white hover:shadow-md"
              )}
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Wird gesendet...</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span>E-Mail senden</span>
                </>
              )}
            </motion.button>
          ) : (
            <motion.button
              onClick={handleOpenMailClient}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-linear-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-lg shadow-xs hover:shadow-md transition-all"
            >
              <Send className="h-4 w-4" />
              <span>E-Mail öffnen</span>
            </motion.button>
          )}
        </div>
      </div>

      {/* Instructions Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="px-4 py-3 bg-muted/50 border-t border-border"
      >
        <div className="flex items-start gap-2">
          <Sparkles className="h-3 w-3 text-primary-400 mt-0.5 animate-pulse" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            {isEditing
              ? "Bearbeiten Sie die E-Mail nach Ihren Wünschen. Klicken Sie außerhalb oder drücken Sie Esc zum Speichern."
              : "Klicken Sie auf den Text, um die E-Mail zu bearbeiten und Ihre persönlichen Daten einzufügen."}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
